import { useTranslation } from "react-i18next";
import React, { useEffect, useState, useRef, Component } from 'react';
import { ChevronDownIcon, PlusIcon, ChevronLeftIcon } from 'lucide-react';
interface PaymentMethodsFiltersProps {
  paymentMethodCategories: string[];
  onPaymentMethodCategoriesChange: (categories: string[]) => void;
  paymentMethods: string[];
  onPaymentMethodsChange: (methods: string[]) => void;
  cardBrands: string[];
  onCardBrandsChange: (brands: string[]) => void;
  fundingSourceTypes: string[];
  onFundingSourceTypesChange: (types: string[]) => void;
  cardTypes: string[];
  onCardTypesChange: (types: string[]) => void;
  inputMethods: string[];
  onInputMethodsChange: (methods: string[]) => void;
  interactionTypes: string[];
  onInteractionTypesChange: (types: string[]) => void;
  networkTokenStatuses: string[];
  onNetworkTokenStatusesChange: (statuses: string[]) => void;
}
export function PaymentMethodsFilters({
  paymentMethodCategories,
  onPaymentMethodCategoriesChange,
  paymentMethods,
  onPaymentMethodsChange,
  cardBrands,
  onCardBrandsChange,
  fundingSourceTypes,
  onFundingSourceTypesChange,
  cardTypes,
  onCardTypesChange,
  inputMethods,
  onInputMethodsChange,
  interactionTypes,
  onInteractionTypesChange,
  networkTokenStatuses,
  onNetworkTokenStatusesChange
}: PaymentMethodsFiltersProps) {
  const {
    t
  } = useTranslation("analytics");
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showMethodDropdown, setShowMethodDropdown] = useState(false);
  const [showCardBrandDropdown, setShowCardBrandDropdown] = useState(false);
  const [showFundingSourceDropdown, setShowFundingSourceDropdown] = useState(false);
  const [showCardTypeDropdown, setShowCardTypeDropdown] = useState(false);
  const [showInputMethodDropdown, setShowInputMethodDropdown] = useState(false);
  const [showInteractionTypeDropdown, setShowInteractionTypeDropdown] = useState(false);
  const [showNetworkTokenDropdown, setShowNetworkTokenDropdown] = useState(false);
  const categoryRef = useRef<HTMLDivElement>(null);
  const methodRef = useRef<HTMLDivElement>(null);
  const cardBrandRef = useRef<HTMLDivElement>(null);
  const fundingSourceRef = useRef<HTMLDivElement>(null);
  const cardTypeRef = useRef<HTMLDivElement>(null);
  const inputMethodRef = useRef<HTMLDivElement>(null);
  const interactionTypeRef = useRef<HTMLDivElement>(null);
  const networkTokenRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (categoryRef.current && !categoryRef.current.contains(event.target as Node)) {
        setShowCategoryDropdown(false);
      }
      if (methodRef.current && !methodRef.current.contains(event.target as Node)) {
        setShowMethodDropdown(false);
      }
      if (cardBrandRef.current && !cardBrandRef.current.contains(event.target as Node)) {
        setShowCardBrandDropdown(false);
      }
      if (fundingSourceRef.current && !fundingSourceRef.current.contains(event.target as Node)) {
        setShowFundingSourceDropdown(false);
      }
      if (cardTypeRef.current && !cardTypeRef.current.contains(event.target as Node)) {
        setShowCardTypeDropdown(false);
      }
      if (inputMethodRef.current && !inputMethodRef.current.contains(event.target as Node)) {
        setShowInputMethodDropdown(false);
      }
      if (interactionTypeRef.current && !interactionTypeRef.current.contains(event.target as Node)) {
        setShowInteractionTypeDropdown(false);
      }
      if (networkTokenRef.current && !networkTokenRef.current.contains(event.target as Node)) {
        setShowNetworkTokenDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <>
      {/* Main Filters - Same row as date/granularity */}
      <div className="relative" ref={categoryRef} data-id="element-2069">
        <button onClick={() => setShowCategoryDropdown(!showCategoryDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2070">
          
          <span data-id="element-2071">{t("components.AcceptanceFilters.payment_method_category")}</span>
          {paymentMethodCategories.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2072">
              {paymentMethodCategories.length}
            </span>}
          <ChevronDownIcon size={16} data-id="element-2073" />
        </button>

        {showCategoryDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_category")} options={['Cards', 'Wallets', 'Bank transfers', 'Buy now pay later', 'Cash-based', 'Direct debit']} selectedOptions={paymentMethodCategories} onApply={selected => {
        onPaymentMethodCategoriesChange(selected);
        setShowCategoryDropdown(false);
      }} onBack={null} data-id="element-2074" />}
      </div>

      <div className="relative" ref={methodRef} data-id="element-2075">
        <button onClick={() => setShowMethodDropdown(!showMethodDropdown)} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2076">
          
          <span data-id="element-2077">{t("components.AcceptanceFilters.payment_method")}</span>
          {paymentMethods.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2078">
              {paymentMethods.length}
            </span>}
          <ChevronDownIcon size={16} data-id="element-2079" />
        </button>

        {showMethodDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_payment_method")} options={['Card', 'Apple Pay', 'Google Pay', 'Alipay', 'WeChat Pay', 'GrabPay', 'LINE Pay', 'TrueMoney', 'PromptPay', 'PayPal']} selectedOptions={paymentMethods} onApply={selected => {
        onPaymentMethodsChange(selected);
        setShowMethodDropdown(false);
      }} onBack={null} data-id="element-2080" />}
      </div>

      {/* Secondary Filters - Separate row with smaller buttons */}
      <div className="w-full flex flex-wrap items-center gap-2 mt-3" data-id="element-2081">
        <div className="relative" ref={cardBrandRef} data-id="element-2082">
          <button onClick={() => setShowCardBrandDropdown(!showCardBrandDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2083">
            
            <PlusIcon size={14} data-id="element-2084" />
            <span data-id="element-2085">{t("components.AcceptanceFilters.card_brand")}</span>
            {cardBrands.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2086">
                {cardBrands.length}
              </span>}
          </button>

          {showCardBrandDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_card_brand")} options={['Visa', 'Mastercard', 'American Express', 'Cartes Bancaires', 'Discover', 'eftpos Australia', 'Girocard', 'JCB', 'UnionPay']} selectedOptions={cardBrands} onApply={selected => {
          onCardBrandsChange(selected);
          setShowCardBrandDropdown(false);
        }} onBack={null} data-id="element-2087" />}
        </div>

        <div className="relative" ref={fundingSourceRef} data-id="element-2088">
          <button onClick={() => setShowFundingSourceDropdown(!showFundingSourceDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2089">
            
            <PlusIcon size={14} data-id="element-2090" />
            <span data-id="element-2091">{t("components.AcceptanceFilters.funding_source_type")}</span>
            {fundingSourceTypes.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2092">
                {fundingSourceTypes.length}
              </span>}
          </button>

          {showFundingSourceDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_funding_source")} options={['Card', 'Bank account', 'Stored balance', 'BNPL credit']} selectedOptions={fundingSourceTypes} onApply={selected => {
          onFundingSourceTypesChange(selected);
          setShowFundingSourceDropdown(false);
        }} onBack={null} data-id="element-2093" />}
        </div>

        <div className="relative" ref={cardTypeRef} data-id="element-2094">
          <button onClick={() => setShowCardTypeDropdown(!showCardTypeDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2095">
            
            <PlusIcon size={14} data-id="element-2096" />
            <span data-id="element-2097">{t("components.AcceptanceFilters.card_type")}</span>
            {cardTypes.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2098">
                {cardTypes.length}
              </span>}
          </button>

          {showCardTypeDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_card_type")} options={['Credit card', 'Debit card', 'Prepaid card']} selectedOptions={cardTypes} onApply={selected => {
          onCardTypesChange(selected);
          setShowCardTypeDropdown(false);
        }} onBack={null} data-id="element-2099" />}
        </div>

        <div className="relative" ref={inputMethodRef} data-id="element-2100">
          <button onClick={() => setShowInputMethodDropdown(!showInputMethodDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2101">
            
            <PlusIcon size={14} data-id="element-2102" />
            <span data-id="element-2103">{t("components.PaymentMethodsFilters.input_method")}</span>
            {inputMethods.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2104">
                {inputMethods.length}
              </span>}
          </button>

          {showInputMethodDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_input_method")} options={['In-person (card present)', 'Online (card-not-present)', 'Apple Pay', 'Google Pay']} selectedOptions={inputMethods} onApply={selected => {
          onInputMethodsChange(selected);
          setShowInputMethodDropdown(false);
        }} onBack={null} data-id="element-2105" />}
        </div>

        <div className="relative" ref={interactionTypeRef} data-id="element-2106">
          <button onClick={() => setShowInteractionTypeDropdown(!showInteractionTypeDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2107">
            
            <PlusIcon size={14} data-id="element-2108" />
            <span data-id="element-2109">{t("components.AcceptanceFilters.interaction_type")}</span>
            {interactionTypes.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2110">
                {interactionTypes.length}
              </span>}
          </button>

          {showInteractionTypeDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_interaction_type")} options={['One time', 'Saved card', 'Recurring initial', 'Recurring subsequent', 'Unscheduled']} selectedOptions={interactionTypes} onApply={selected => {
          onInteractionTypesChange(selected);
          setShowInteractionTypeDropdown(false);
        }} onBack={null} data-id="element-2111" />}
        </div>

        <div className="relative" ref={networkTokenRef} data-id="element-2112">
          <button onClick={() => setShowNetworkTokenDropdown(!showNetworkTokenDropdown)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2113">
            
            <PlusIcon size={14} data-id="element-2114" />
            <span data-id="element-2115">{t("components.PaymentMethodsFilters.network_token_status")}</span>
            {networkTokenStatuses.length > 0 && <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full" data-id="element-2116">
                {networkTokenStatuses.length}
              </span>}
          </button>

          {showNetworkTokenDropdown && <CompactDropdown title={t("components.AcceptanceFilters.attrs.filter_by_network_token_status")} options={['Non-tokenized card', 'Network token']} selectedOptions={networkTokenStatuses} onApply={selected => {
          onNetworkTokenStatusesChange(selected);
          setShowNetworkTokenDropdown(false);
        }} onBack={null} data-id="element-2117" />}
        </div>
      </div>
    </>;
}
// Compact Dropdown Component (for checkbox lists)
interface CompactDropdownProps {
  title: string;
  options: string[];
  selectedOptions: string[];
  onApply: (selected: string[]) => void;
  onBack: (() => void) | null;
  isInline?: boolean;
}
function CompactDropdown({
  title,
  options,
  selectedOptions,
  onApply,
  onBack,
  isInline = false
}: CompactDropdownProps) {
  const {
    t
  } = useTranslation("analytics");
  const [tempSelected, setTempSelected] = useState<string[]>(selectedOptions);
  const toggleOption = (option: string) => {
    if (tempSelected.includes(option)) {
      setTempSelected(tempSelected.filter(o => o !== option));
    } else {
      setTempSelected([...tempSelected, option]);
    }
  };
  const content = <>
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3" data-id="element-2118">
        {onBack && <button onClick={onBack} className="p-1 hover:bg-gray-100 rounded" data-id="element-2119">
            <ChevronLeftIcon size={18} className="text-gray-600" data-id="element-2120" />
          </button>}
        <h3 className="text-base font-semibold text-gray-900" data-id="element-2121">{title}</h3>
      </div>
      <div className="flex-1 overflow-y-auto py-1" data-id="element-2122">
        {options.map(option => <label key={option} className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 cursor-pointer" data-id="element-2123">
        
            <input type="checkbox" checked={tempSelected.includes(option)} onChange={() => toggleOption(option)} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-2124" />
        
            <span className="text-base text-gray-900" data-id="element-2125">{option}</span>
          </label>)}
      </div>
      <div className="px-4 py-3 border-t border-gray-200" data-id="element-2126">
        <button onClick={() => onApply(tempSelected)} className="w-full px-4 py-2.5 text-base font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg" data-id="element-2127">
        
          {t("components.content.apply")}
        </button>
      </div>
    </>;
  if (isInline) {
    return <div className="flex flex-col max-h-96" data-id="element-2128">{content}</div>;
  }
  return <div className="absolute left-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 flex flex-col" data-id="element-2129">
      {content}
    </div>;
}